import { Options } from 'ol/interaction/Pointer';
import { MapBrowserEvent } from 'ol';
import TrackLineFeature from './TrackLineFeature';
import Point from 'ol/geom/Point';
import BaseEvent from 'ol/events/Event';
import PreUndoEvent from './PreUndoEvent';
import { Renderer } from './Renderer';
import { TrackEntity } from './TrackEntity';
import * as _ from 'lodash'
import PreUndoData from './PreUndoData';
import DeleteTrackData from './DeleteTrackData';
import SplitHelper from './SplitHelper';
import EventRememberingPointerInteraction from './EventRememberingPointerInteraction';

interface SplitTrackOptions {
    renderer: Renderer<any>;
}

export enum SplitTrackEvents {
    newtrack = 'newtrack'
}

export class NewTrackEvent extends BaseEvent {
    oldTrack: TrackEntity
    newTrack: TrackEntity
    constructor(oldTrack: TrackEntity, newTrack: TrackEntity) {
        super(SplitTrackEvents.newtrack)
        this.oldTrack = oldTrack
        this.newTrack = newTrack
    }
}

export default class SplitTrack extends EventRememberingPointerInteraction {
    options: SplitTrackOptions

    constructor(options: SplitTrackOptions, superOptions?: Options) {
        super(superOptions)
        this.options = options
    }

    handleEvent(event: MapBrowserEvent<any>) {
        if (event.type === 'singleclick') {
            return this.handleSingleClick(event)
        }
        return super.handleEvent(event);
    }

    handleSingleClick(event: MapBrowserEvent<any>) {
        const linesAtPoint = event.map.getFeaturesAtPixel(event.pixel).filter(f => f instanceof TrackLineFeature) as TrackLineFeature[]
        if (linesAtPoint.length > 0) {
            return this.doSplit(event, linesAtPoint[0])
        }

        return super.handleEvent(event)
    }

    doSplit(event: MapBrowserEvent<any>, trackLineFeature: TrackLineFeature) {
        const allTracks = trackLineFeature.track.getSegments()
        const thisTrackIndex = allTracks.indexOf(trackLineFeature)

        const split = SplitHelper.splitLineAt(trackLineFeature.getGeometry(), event.coordinate)

        const newTrack = new TrackEntity(this.options.renderer)
        const restoreData = trackLineFeature.track.getRestoreData(this.options.renderer) // restore this track to how it was previously
        const deleteData = new DeleteTrackData(newTrack.value) // delete this track
        const undoData: PreUndoData[] = [restoreData, deleteData]
        this.dispatchEvent(new PreUndoEvent(undoData, 'split track'))
        trackLineFeature.setGeometry(split.firstTrackGeometry)
        newTrack.addSegment(split.secondTrackGeometry)

        const migratePoint = thisTrackIndex + 1
        const lineFeaturesToMigrate = _.drop(trackLineFeature.track.getSegments(), migratePoint)

        for (const f of lineFeaturesToMigrate) {
            trackLineFeature.track.removeSegment(f)
            newTrack.addSegment(f.getGeometry())
        }

        const dotFeaturesToMigrate = _.drop(trackLineFeature.track.getDots(), migratePoint)
        for (const f of dotFeaturesToMigrate) {
            trackLineFeature.track.removeDot(f)
            newTrack.addDot(f.getGeometry())
        }

        const newDotFormer = new Point(split.firstTrackGeometry.getLastCoordinate())
        const newDotLatter = new Point(split.secondTrackGeometry.getFirstCoordinate())
        trackLineFeature.track.addDot(newDotFormer)
        newTrack.addDot(newDotLatter, 0)

        this.dispatchEvent(new NewTrackEvent(trackLineFeature.track, newTrack))

        return true
    }


}